import { useState } from "react";
import { Link } from "react-router-dom";
import { Check, Download as DownloadIcon, Share, MoreVertical, Monitor } from "lucide-react";
import { MarketingHeader, MarketingFooter, Primary } from "../components/layout/MarketingShell";
import useInstallPrompt from "../hooks/useInstallPrompt";

/* Bicarra installs as a progressive web app. There is no App Store build, so
   this page must be honest about that while still getting people installed.
   Chromium gets a real button; iOS gets the manual steps, because Safari never
   fires an install event. */

function Step({ n, children }) {
  return (
    <li className="flex gap-3 text-[15px] leading-relaxed text-b2-inksoft">
      <span className="flex-none w-6 h-6 rounded-full bg-b2-wash text-b2-washink
                       text-[12.5px] font-semibold inline-flex items-center justify-center">
        {n}
      </span>
      <span>{children}</span>
    </li>
  );
}

function Card({ title, icon, children }) {
  return (
    <div className="rounded-[14px] bg-b2-surface border border-b2-line p-7">
      <div className="flex items-center gap-2.5 mb-4">
        <span className="text-b2-muted">{icon}</span>
        <h2 className="text-[17px] font-semibold text-b2-ink">{title}</h2>
      </div>
      {children}
    </div>
  );
}

export default function Download() {
  const { platform, installed, canPrompt, promptInstall } = useInstallPrompt();
  const [outcome, setOutcome] = useState(null);

  async function onInstall() {
    setOutcome(await promptInstall());
  }

  return (
    <div className="b2 min-h-screen bg-b2-canvas text-b2-ink font-sans flex flex-col">
      <MarketingHeader />

      <main className="flex-1 max-w-[760px] w-full mx-auto px-6 pt-14 pb-24" data-testid="download-page">
        <h1 className="text-[36px] sm:text-[42px] leading-[1.1] font-bold tracking-[-0.03em]">
          Put Bicarra on your home screen
        </h1>
        <p className="mt-4 text-[17px] leading-relaxed text-b2-inksoft">
          Bicarra installs straight from the browser. It opens in its own window with no
          address bar, loads faster on return visits, and sits beside your other apps.
          Nothing to download from a store, and it updates itself.
        </p>

        {installed ? (
          <div data-testid="already-installed"
               className="mt-8 rounded-[14px] bg-b2-wash border border-b2-line p-6 flex items-start gap-3">
            <Check className="w-5 h-5 text-b2-washink flex-none mt-0.5" strokeWidth={2.2} />
            <div>
              <p className="text-[15px] font-semibold text-b2-washink">Already installed</p>
              <p className="mt-1 text-[14.5px] text-b2-inksoft">
                You are looking at the installed app right now.
              </p>
            </div>
          </div>
        ) : canPrompt ? (
          <div className="mt-8">
            <button
              type="button" onClick={onInstall} data-testid="install-app-btn"
              className="inline-flex items-center justify-center gap-2 h-12 px-7 rounded-[10px]
                         bg-b2-indigo hover:bg-b2-indigohover text-white text-[15px]
                         font-semibold transition-colors"
            >
              <DownloadIcon className="w-[18px] h-[18px]" strokeWidth={2} />
              Install Bicarra
            </button>
            {outcome === "dismissed" && (
              <p className="mt-3 text-[14px] text-b2-muted">
                No problem. The option stays in your browser menu whenever you want it.
              </p>
            )}
          </div>
        ) : null}

        <div className="mt-12 space-y-4">
          {platform === "ios" && (
            <Card title="iPhone and iPad" icon={<Share className="w-[18px] h-[18px]" strokeWidth={1.9} />}>
              <ol className="space-y-3">
                <Step n="1">Open this page in <strong className="font-semibold text-b2-ink">Safari</strong>. Chrome on iOS cannot install apps.</Step>
                <Step n="2">Tap the <strong className="font-semibold text-b2-ink">Share</strong> button, the square with an arrow pointing up.</Step>
                <Step n="3">Scroll down and tap <strong className="font-semibold text-b2-ink">Add to Home Screen</strong>.</Step>
                <Step n="4">Tap <strong className="font-semibold text-b2-ink">Add</strong>. Bicarra appears with your other apps.</Step>
              </ol>
            </Card>
          )}

          {platform === "android" && (
            <Card title="Android" icon={<MoreVertical className="w-[18px] h-[18px]" strokeWidth={1.9} />}>
              <ol className="space-y-3">
                <Step n="1">Tap the <strong className="font-semibold text-b2-ink">three dots</strong> in the top right of Chrome.</Step>
                <Step n="2">Tap <strong className="font-semibold text-b2-ink">Add to Home screen</strong> or <strong className="font-semibold text-b2-ink">Install app</strong>.</Step>
                <Step n="3">Confirm. Bicarra opens in its own window from then on.</Step>
              </ol>
            </Card>
          )}

          {platform === "desktop" && (
            <Card title="Mac and Windows" icon={<Monitor className="w-[18px] h-[18px]" strokeWidth={1.9} />}>
              <ol className="space-y-3">
                <Step n="1">Use <strong className="font-semibold text-b2-ink">Chrome</strong> or <strong className="font-semibold text-b2-ink">Edge</strong>. Safari and Firefox do not install web apps on the desktop.</Step>
                <Step n="2">Look for the small install icon at the right end of the address bar, a monitor with a downward arrow.</Step>
                <Step n="3">If you cannot see it, open the browser menu and look for <strong className="font-semibold text-b2-ink">Install page as app</strong>.</Step>
              </ol>
            </Card>
          )}

          <p className="text-[14px] text-b2-muted pt-1">
            On a different device? The steps live under your browser menu, usually named
            Install app or Add to Home Screen.
          </p>
        </div>

        <div className="mt-14 rounded-[14px] bg-b2-surface border border-b2-line p-7">
          <h2 className="text-[17px] font-semibold">WordPress plugin</h2>
          <p className="mt-2 text-[15px] leading-relaxed text-b2-inksoft">
            Embed your Bicarra chatbot on any WordPress site. Install the plugin, paste your
            chatbot ID, and the widget appears across the site.
          </p>
          <a href="/bicarra-chat.zip" download data-testid="download-wordpress"
             className="mt-5 inline-flex items-center justify-center gap-2 h-11 px-6 rounded-[10px]
                        bg-white border border-b2-linestrong text-b2-ink text-[15px] font-semibold
                        hover:border-b2-indigo transition-colors">
            <DownloadIcon className="w-[18px] h-[18px]" strokeWidth={2} />
            Download plugin (v1.1.0)
          </a>
          <p className="mt-3 text-[13px] text-b2-faint">
            Needs WordPress 5.0 or newer and PHP 7.4 or newer. After installing, go to
            Settings then Bicarra Chat in your WordPress admin.
          </p>
        </div>

        <div className="mt-14 pt-10 border-t border-b2-line">
          <h2 className="text-[19px] font-semibold tracking-[-0.02em]">No account yet?</h2>
          <p className="mt-2 text-[15px] text-b2-inksoft">
            Installing gives you the app. You will still need an account to use it.
          </p>
          <div className="mt-5 flex items-center gap-3 flex-wrap">
            <Primary to="/register" testid="download-register">Create an account</Primary>
            <Link to="/pricing" className="text-[14.5px] font-medium text-b2-inksoft hover:text-b2-ink">
              See pricing
            </Link>
          </div>
        </div>
      </main>

      <MarketingFooter />
    </div>
  );
}
